import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Header,
  Segment,
  Dropdown,
  Button,
  Table,
  Input,
  Message,
  Icon,
  Loader,
  Form,
  Modal,
} from "semantic-ui-react";

const API = process.env.REACT_APP_API_URL || "";

// format YYYY-MM-DD from a Date (UTC-safe)
function formatDateISO(dt) {
  const y = dt.getUTCFullYear();
  const m = `${dt.getUTCMonth() + 1}`.padStart(2, "0");
  const d = `${dt.getUTCDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// compute recent Thursdays matching server behaviour (UTC)
function getRecentThursdays(n = 16) {
  const out = [];
  const now = new Date();
  const todayUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  const todayDow = todayUTC.getUTCDay(); // 0=Sun..6=Sat
  const daysUntilThu = (4 - todayDow + 7) % 7;
  const nextThu = new Date(
    Date.UTC(
      todayUTC.getUTCFullYear(),
      todayUTC.getUTCMonth(),
      todayUTC.getUTCDate() + daysUntilThu
    )
  );
  out.push(formatDateISO(nextThu));
  let d = new Date(nextThu);
  for (let i = 1; i < n; i++) {
    d = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 7)
    );
    out.push(formatDateISO(d));
  }
  return out;
}

// determine which cell corresponds to "today" relative to the weekending (weekISO is Thursday)
function getTodayKeyForWeek(weekISO) {
  if (!weekISO) return "Thursday";
  const parts = weekISO.split("-");
  if (parts.length < 3) return "Thursday";
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  const weekThuUTC = new Date(Date.UTC(y, m, d));
  const now = new Date();
  const nowUTCDate = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  const diffDays = Math.round(
    (nowUTCDate - weekThuUTC) / (1000 * 60 * 60 * 24)
  );
  switch (diffDays) {
    case 0:
      return "Thursday";
    case 1:
      return "Friday";
    case 4:
      return "Monday";
    case 5:
      return "Tuesday";
    case 6:
      return "Wednesday";
    default:
      return "Thursday";
  }
}

export default function InputStats() {
  const thursdays = useMemo(() => getRecentThursdays(20), []);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [statsMeta, setStatsMeta] = useState([]);
  const [metaError, setMetaError] = useState(null);

  // view mode: 'daily' | 'weekly'
  const [viewMode, setViewMode] = useState("daily");

  // Daily state
  const [dailyWeek, setDailyWeek] = useState(thursdays[0]);
  const [dailyTable, setDailyTable] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyMessage, setDailyMessage] = useState(null);

  // Today modal (per-row quick entry) - used by daily
  const [todayModalOpen, setTodayModalOpen] = useState(false);
  const [todayModalStat, setTodayModalStat] = useState(null);
  const [todayModalValue, setTodayModalValue] = useState("");
  const [todayModalSubmitting, setTodayModalSubmitting] = useState(false);

  // Weekly state
  const [weeklyWeek, setWeeklyWeek] = useState(thursdays[0]);
  const [weeklyValues, setWeeklyValues] = useState({}); // map statId -> value (string) for selected week
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyMessage, setWeeklyMessage] = useState(null);
  // weekly quick-entry modal
  const [weeklyModalOpen, setWeeklyModalOpen] = useState(false);
  const [weeklyModalStat, setWeeklyModalStat] = useState(null);
  const [weeklyModalValue, setWeeklyModalValue] = useState("");
  const [weeklyModalSubmitting, setWeeklyModalSubmitting] = useState(false);
  const [weeklyModalTargetUser, setWeeklyModalTargetUser] = useState(null);

  // history list (shown when user requests)
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // track per-row weekly-saving state (array of statIds currently saving weekly)
  const [savingWeeklyIds, setSavingWeeklyIds] = useState([]);

  // load user + assigned stats
  useEffect(() => {
    (async () => {
      try {
        const uRes = await fetch(`${API}/api/user`, { credentials: "include" });
        if (!uRes.ok) throw new Error("Failed to load user info");
        const uJson = await uRes.json();
        setUser(uJson);
        setIsAdmin(uJson.role === "admin");

        if (uJson.role === "admin") {
          const sr = await fetch(`${API}/api/stats/all`, {
            credentials: "include",
          });
          if (!sr.ok) throw new Error("Failed to load stats metadata (admin)");
          const sj = await sr.json();
          setStatsMeta(Array.isArray(sj) ? sj : []);
        } else {
          const ar = await fetch(`${API}/api/stats/assigned`, {
            credentials: "include",
          });
          if (ar.ok) {
            const aj = await ar.json();
            setStatsMeta(Array.isArray(aj) ? aj : []);
          } else {
            setMetaError(
              "Server must provide GET /api/stats/assigned for non-admin users"
            );
          }
        }
      } catch (err) {
        setMetaError(err.message || String(err));
      }
    })();
  }, []);

  // load daily table when week or assigned stats change
  useEffect(() => {
    if (!statsMeta || !statsMeta.length) {
      setDailyTable([]);
      return;
    }
    if (viewMode === "daily") loadDailyTable(dailyWeek, statsMeta);
  }, [dailyWeek, statsMeta, viewMode]);

  // load weekly values for selected week when week or stats change
  useEffect(() => {
    if (!statsMeta || !statsMeta.length) {
      setWeeklyValues({});
      return;
    }
    if (viewMode === "weekly") loadWeeklyValuesForWeek(weeklyWeek, statsMeta);
  }, [weeklyWeek, statsMeta, viewMode]);

  // load daily data per stat (prefers stat_id query param)
  async function loadDailyTable(week, stats) {
    setDailyLoading(true);
    setDailyMessage(null);
    try {
      const rows = [];
      for (const s of stats) {
        const params = new URLSearchParams();
        if (s.id) params.set("stat_id", String(s.id));
        else params.set("stat", s.short_id || "");
        params.set("date", week);
        const url = `${API}/services/getDailyStats?${params.toString()}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          rows.push({
            statId: s.id,
            short_id: s.short_id,
            username: s.username,
            full_name: s.full_name,
            type: s.type,
            is_calculated: s.is_calculated,
            Thursday: "",
            Friday: "",
            Monday: "",
            Tuesday: "",
            Wednesday: "",
            Quota: "",
            dirty: false, // track changes
          });
          continue;
        }
        const json = await res.json();
        rows.push({
          statId: s.id,
          short_id: s.short_id,
          username: s.username,
          full_name: s.full_name,
          type: s.type,
          is_calculated: s.is_calculated,
          Thursday: json.Thursday || "",
          Friday: json.Friday || "",
          Monday: json.Monday || "",
          Tuesday: json.Tuesday || "",
          Wednesday: json.Wednesday || "",
          Quota: json.Quota || "",
          dirty: false, // track changes
        });
      }
      setDailyTable(rows);
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
      setDailyTable([]);
    } finally {
      setDailyLoading(false);
    }
  }

  async function saveDailyTable() {
    setDailyLoading(true);
    setDailyMessage(null);
    try {
      // Only send rows that have been modified (dirty) and are not calculated
      const modified = dailyTable.filter((r) => r.dirty && !r.is_calculated);
      if (modified.length === 0) {
        setDailyMessage({ type: "info", text: "No modified rows to save" });
        return;
      }

      const payload = modified.map((r) => ({
        StatID: r.statId || null,
        Name: r.short_id,
        Thursday: r.Thursday || "",
        Friday: r.Friday || "",
        Monday: r.Monday || "",
        Tuesday: r.Tuesday || "",
        Wednesday: r.Wednesday || "",
        Quota: r.Quota || "",
      }));

      const res = await fetch(
        `${API}/services/save7R?thisWeek=${encodeURIComponent(dailyWeek)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const txt = await res.text();
      if (res.ok) {
        setDailyMessage({ type: "success", text: txt || "Saved" });
        // Refresh the table so we don't overwrite others' changes; this resets dirty flags
        await loadDailyTable(dailyWeek, statsMeta);
      } else {
        setDailyMessage({ type: "error", text: txt || "Failed to save" });
      }
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
    } finally {
      setDailyLoading(false);
    }
  }

  // Save a single row (per-line save). Sends only that stat and then refreshes that row
  async function saveDailyRow(row) {
    if (!row || row.is_calculated) return;
    setDailyLoading(true);
    setDailyMessage(null);
    try {
      const payload = [
        {
          StatID: row.statId || null,
          Name: row.short_id,
          Thursday: row.Thursday || "",
          Friday: row.Friday || "",
          Monday: row.Monday || "",
          Tuesday: row.Tuesday || "",
          Wednesday: row.Wednesday || "",
          Quota: row.Quota || "",
        },
      ];
      const res = await fetch(
        `${API}/services/save7R?thisWeek=${encodeURIComponent(dailyWeek)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const txt = await res.text();
      if (res.ok) {
        setDailyMessage({ type: "success", text: txt || "Saved row" });
        // Refresh just the table (server returns canonical values)
        await loadDailyTable(dailyWeek, statsMeta);
      } else {
        setDailyMessage({ type: "error", text: txt || "Failed to save row" });
      }
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
    } finally {
      setDailyLoading(false);
    }
  }

  // Open the weekly modal prefilled from a daily row.
  // Prefers the canonical weekly value (weeklyValues[statId]) when present,
  // otherwise falls back to computing the cumulative daily total.
  function openWeeklyModalFromDaily(row) {
    if (!row) return;
    const statId = row.statId || null;
    if (!statId) return;

    // Try to find metadata for the stat
    const meta = statsMeta.find((s) => String(s.id) === String(statId)) || {
      id: statId,
      short_id: row.short_id,
      full_name: row.full_name,
      type: row.type,
      value_type: "number",
    };

    // If there is an existing weekly canonical value for this stat/week, use it
    const canonicalWeekly =
      weeklyValues && weeklyValues[statId] !== undefined
        ? weeklyValues[statId]
        : null;

    let prefillValue = "";
    const total =
      (parseFloat(row.Thursday) || 0) +
      (parseFloat(row.Friday) || 0) +
      (parseFloat(row.Monday) || 0) +
      (parseFloat(row.Tuesday) || 0) +
      (parseFloat(row.Wednesday) || 0);

    const valueType = meta.value_type || meta.valueType || "number";
    switch (valueType) {
      case "currency":
        prefillValue = total.toFixed(2);
        break;
      case "percentage":
        prefillValue = Number(total).toFixed(2);
        break;
      case "number":
      default:
        prefillValue = String(Math.round(total));
        break;
    }
    // Prefill modal state so the admin can edit/confirm and save via the same modal flow
    setWeeklyModalStat(meta);
    setWeeklyModalValue(prefillValue);
    // If admin editing a personal stat for another user, try to set target user from metadata
    const assignedUid =
      meta.user_id ||
      meta.owner_id ||
      (Array.isArray(meta.user_ids) && meta.user_ids.length
        ? meta.user_ids[0]
        : null) ||
      null;
    setWeeklyModalTargetUser(assignedUid);

    // Use the currently-selected dailyWeek as the week to save (modal will send that date)
    setWeeklyWeek(dailyWeek);

    // Open the existing weekly modal
    setWeeklyModalOpen(true);
  }

  // Save the cumulative daily total as the canonical weekly value for the current week
  // (kept for backward compatibility — the new UX uses modal instead)
  async function saveWeeklyFromDaily(row) {
    // legacy path; still works if you prefer direct save without modal
    if (!row || row.is_calculated) {
      // allow saving even if is_calculated? per request we'll allow saving, so remove early return
      // (but keep a guard in case row missing)
    }
    const statId = row.statId || null;
    if (!statId) return;

    // compute cumulative total the same way we display it
    const total =
      (parseFloat(row.Thursday) || 0) +
      (parseFloat(row.Friday) || 0) +
      (parseFloat(row.Monday) || 0) +
      (parseFloat(row.Tuesday) || 0) +
      (parseFloat(row.Wednesday) || 0);

    const meta = statsMeta.find((s) => String(s.id) === String(statId)) || {};
    const valueType = meta.value_type || meta.valueType || "number";

    let valueStr = "";
    switch (valueType) {
      case "currency":
        valueStr = total.toFixed(2);
        break;
      case "percentage":
        valueStr = Number(total).toFixed(2);
        break;
      case "number":
      default:
        valueStr = String(Math.round(total));
        break;
    }

    setSavingWeeklyIds((prev) => [...prev, String(statId)]);
    setDailyMessage(null);
    try {
      const payload = {
        stat_id: statId,
        date: dailyWeek,
        value: valueStr,
      };

      const res = await fetch(`${API}/services/logWeeklyStats`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let json;
      if (text) {
        try {
          json = JSON.parse(text);
        } catch {
          json = { message: text };
        }
      }

      if (!res.ok) {
        const msg =
          json && json.message ? json.message : text || `HTTP ${res.status}`;
        setDailyMessage({ type: "error", text: msg });
      } else {
        const successMsg =
          json && json.message ? json.message : "Weekly value saved";
        setDailyMessage({ type: "success", text: successMsg });
        // Refresh both weekly values and daily table so UI reflects canonical saved value
        await loadWeeklyValuesForWeek(weeklyWeek, statsMeta);
        await loadDailyTable(dailyWeek, statsMeta);
      }
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
    } finally {
      setSavingWeeklyIds((prev) =>
        prev.filter((id) => String(id) !== String(statId))
      );
    }
  }

  // Revert a single row to server copy (discard local edits)
  async function revertDailyRow(row) {
    if (!row) return;
    setDailyLoading(true);
    try {
      // Re-load the table (or could fetch single stat)
      await loadDailyTable(dailyWeek, statsMeta);
      setDailyMessage({ type: "info", text: "Changes reverted" });
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
    } finally {
      setDailyLoading(false);
    }
  }

  // Open quick-entry modal for a stat row (daily)
  function openTodayModal(row) {
    setTodayModalStat(row);
    setTodayModalValue("");
    setTodayModalOpen(true);
  }

  // Submit today's single value; payload includes StatID so backend updates only that stat
  async function submitTodayValue() {
    if (!todayModalStat) return;
    const dayKey = getTodayKeyForWeek(dailyWeek);
    setDailyTable((prev) =>
      prev.map((r) =>
        r.statId === todayModalStat.statId
          ? { ...r, [dayKey]: todayModalValue, dirty: true }
          : r
      )
    );
    setTodayModalSubmitting(true);
    try {
      const payload = [
        {
          StatID: todayModalStat.statId || null,
          Name: todayModalStat.short_id,
          Thursday: dayKey === "Thursday" ? todayModalValue : "",
          Friday: dayKey === "Friday" ? todayModalValue : "",
          Monday: dayKey === "Monday" ? todayModalValue : "",
          Tuesday: dayKey === "Tuesday" ? todayModalValue : "",
          Wednesday: dayKey === "Wednesday" ? todayModalValue : "",
          Quota: todayModalStat.Quota || "",
        },
      ];
      const res = await fetch(
        `${API}/services/save7R?thisWeek=${encodeURIComponent(dailyWeek)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const txt = await res.text();
      if (!res.ok) {
        setDailyMessage({
          type: "error",
          text: txt || "Failed to save today's value",
        });
      } else {
        setDailyMessage({
          type: "success",
          text: txt || "Saved today's value",
        });
        await loadDailyTable(dailyWeek, statsMeta);
      }
    } catch (err) {
      setDailyMessage({ type: "error", text: String(err) });
    } finally {
      setTodayModalSubmitting(false);
      setTodayModalOpen(false);
    }
  }

  // ---------- WEEKLY helpers ----------
  // load weekly history for a single stat (returns array of {Weekending, Value})
  async function loadWeeklyHistoryForStat(statOrId) {
    setHistoryLoading(true);
    setHistoryList([]);
    setWeeklyMessage(null);
    try {
      let statId = null;
      let assignedUid = null;

      if (!statOrId) {
        throw new Error("stat id required");
      }

      if (typeof statOrId === "number" || typeof statOrId === "string") {
        statId = String(statOrId);
      } else if (typeof statOrId === "object") {
        // prefer id field names used in repo: id or statId
        statId = String(statOrId.id || statOrId.statId || statOrId.ID);
        // possible assigned user id fields
        assignedUid =
          statOrId.user_id ||
          statOrId.owner_id ||
          (Array.isArray(statOrId.user_ids) && statOrId.user_ids.length
            ? statOrId.user_ids[0]
            : null);
      }

      if (!statId) throw new Error("stat id missing");

      const params = new URLSearchParams();
      params.set("stat_id", statId);

      // include user_id when admin and assignedUid present
      if (isAdmin && assignedUid) {
        params.set("user_id", String(assignedUid));
      }

      const res = await fetch(
        `${API}/services/getWeeklyStats?${params.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setHistoryList(Array.isArray(json) ? json : []);
    } catch (err) {
      setHistoryList([]);
      setWeeklyMessage({ type: "error", text: String(err) });
    } finally {
      setHistoryLoading(false);
    }
  }

  // load values for a specific week for all statsMeta and populate weeklyValues map (statId -> value)
  async function loadWeeklyValuesForWeek(weekISO, stats) {
    setWeeklyLoading(true);
    setWeeklyMessage(null);
    try {
      const map = {};
      for (const s of stats) {
        // default empty for missing id
        if (!s.id) {
          map[s.short_id] = "";
          continue;
        }

        const params = new URLSearchParams();
        params.set("stat_id", String(s.id));

        // If current viewer is admin and the stat metadata contains an assigned user id,
        // include user_id so server returns that user's personal series.
        // Accept common possible field names (user_id, owner_id, or user_ids array).
        const assignedUid =
          s.user_id ||
          s.owner_id ||
          (Array.isArray(s.user_ids) && s.user_ids.length
            ? s.user_ids[0]
            : null);

        if (isAdmin && assignedUid) {
          params.set("user_id", String(assignedUid));
        }

        const res = await fetch(
          `${API}/services/getWeeklyStats?${params.toString()}`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) {
          map[s.id] = "";
          continue;
        }

        const series = await res.json(); // array of {Weekending, Value}
        // Find the entry matching the selected week
        const found = (series || []).find(
          (r) =>
            r.Weekending === weekISO ||
            r.week_ending === weekISO ||
            r.WeekEnding === weekISO
        );
        map[s.id] = found ? found.Value : "";
      }
      setWeeklyValues(map);
    } catch (err) {
      setWeeklyMessage({ type: "error", text: String(err) });
      setWeeklyValues({});
    } finally {
      setWeeklyLoading(false);
    }
  }

  // open weekly quick-entry modal for a stat
  function openWeeklyModal(stat) {
    // stat should include .id and .user_id (if assigned) and possibly .division_id
    setWeeklyModalStat(stat);
    setWeeklyModalValue(weeklyValues[stat.id] || "");
    // use the single DB-backed field name user_id
    const assignedUid = stat.user_id || null;
    console.log("uid: ", assignedUid);
    setWeeklyModalTargetUser(assignedUid);

    setWeeklyModalOpen(true);
  }

  // submit single weekly value for the selected week (sends stat_id/date/value to new endpoint)
  async function submitWeeklySingle() {
    if (!weeklyModalStat) return;
    setHistoryLoading(false);
    setWeeklyModalSubmitting(true);
    setWeeklyMessage(null);
    try {
      const statId = weeklyModalStat.id || weeklyModalStat.statId || null;
      if (!statId) throw new Error("stat id missing for selected stat");

      const payload = {
        stat_id: statId,
        date: weeklyWeek, // currently-selected week on the page
        value: weeklyModalValue,
      };

      // For divisional/main stats, include division_id if present in metadata
      if (
        isAdmin &&
        (weeklyModalStat.type === "divisional" ||
          weeklyModalStat.type === "main")
      ) {
        const divisionId = weeklyModalStat.division_id || null;
        if (divisionId) {
          payload.division_id = divisionId;
        }
        // if no division_id provided, backend will try to resolve from stats table
      }

      // If admin is editing a personal stat that belongs to another user, include user_id
      if (
        isAdmin &&
        (weeklyModalStat.type === "personal" || !weeklyModalStat.type) &&
        weeklyModalTargetUser
      ) {
        payload.user_id = weeklyModalTargetUser;
      }

      const res = await fetch(`${API}/services/logWeeklyStats`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let json;
      if (text) {
        try {
          json = JSON.parse(text);
        } catch {
          json = { message: text };
        }
      }

      if (!res.ok) {
        const msg =
          json && json.message ? json.message : text || `HTTP ${res.status}`;
        setWeeklyMessage({ type: "error", text: msg });
        setWeeklyModalSubmitting(false);
        // close the modal to match previous behaviour
        setWeeklyModalOpen(false);
        return;
      }

      const successMsg =
        json && json.message ? json.message : "Weekly value saved";
      setWeeklyMessage({ type: "success", text: successMsg });

      setWeeklyModalOpen(false);
      setWeeklyModalValue("");
      setWeeklyModalTargetUser(null);

      // refresh displayed values for the selected week
      await loadWeeklyValuesForWeek(weeklyWeek, statsMeta);
    } catch (err) {
      setWeeklyMessage({ type: "error", text: String(err) });
    } finally {
      setWeeklyModalSubmitting(false);
    }
  }

  // ---------- RENDER helpers ----------
  function renderTopSwitcher() {
    return (
      <Segment clearing>
        <Button.Group>
          <Button
            active={viewMode === "daily"}
            onClick={() => setViewMode("daily")}
          >
            Daily 7R
          </Button>
          <Button
            active={viewMode === "weekly"}
            onClick={() => setViewMode("weekly")}
          >
            Weekly
          </Button>
        </Button.Group>
      </Segment>
    );
  }

  function renderDailyTable() {
    if (dailyLoading) return <Loader active inline="centered" />;
    if (!dailyTable.length)
      return <Message content="No assigned stats to display" />;
    return (
      <>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Stat</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Thursday</Table.HeaderCell>
              <Table.HeaderCell>Friday</Table.HeaderCell>
              <Table.HeaderCell>Monday</Table.HeaderCell>
              <Table.HeaderCell>Tuesday</Table.HeaderCell>
              <Table.HeaderCell>Wednesday</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {dailyTable.map((r) => {
              const total =
                (parseFloat(r.Thursday) || 0) +
                (parseFloat(r.Friday) || 0) +
                (parseFloat(r.Monday) || 0) +
                (parseFloat(r.Tuesday) || 0) +
                (parseFloat(r.Wednesday) || 0);

              // Find the stat metadata to get value_type for proper formatting
              const meta =
                statsMeta.find((s) => String(s.id) === String(r.statId)) || {};
              const valueType = meta.value_type || meta.valueType || "number";

              // Format total based on value_type
              let formattedTotal = "";
              switch (valueType) {
                case "currency":
                  formattedTotal = total.toFixed(2);
                  break;
                case "percentage":
                  formattedTotal = total.toFixed(2);
                  break;
                case "number":
                default:
                  formattedTotal = String(Math.round(total));
                  break;
              }

              return (
                <Table.Row key={r.statId || r.short_id}>
                  <Table.Cell>
                    <strong>
                      {r.short_id} - {r.username.toUpperCase()}
                    </strong>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {r.full_name}
                    </div>
                  </Table.Cell>
                  {/* <Table.Cell>{r.username}</Table.Cell> */}
                  <Table.Cell>{r.type}</Table.Cell>
                  <Table.Cell>
                    {r.is_calculated ? (
                      <span>{r.Thursday || ""}</span>
                    ) : (
                      <Input
                        fluid
                        value={r.Thursday || ""}
                        onChange={(e) =>
                          setDailyTable((prev) =>
                            prev.map((x) =>
                              String(x.statId) === String(r.statId)
                                ? {
                                    ...x,
                                    Thursday: e.target.value,
                                    dirty: true,
                                  }
                                : x
                            )
                          )
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {r.is_calculated ? (
                      <span>{r.Friday || ""}</span>
                    ) : (
                      <Input
                        fluid
                        value={r.Friday || ""}
                        onChange={(e) =>
                          setDailyTable((prev) =>
                            prev.map((x) =>
                              String(x.statId) === String(r.statId)
                                ? { ...x, Friday: e.target.value, dirty: true }
                                : x
                            )
                          )
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {r.is_calculated ? (
                      <span>{r.Monday || ""}</span>
                    ) : (
                      <Input
                        fluid
                        value={r.Monday || ""}
                        onChange={(e) =>
                          setDailyTable((prev) =>
                            prev.map((x) =>
                              String(x.statId) === String(r.statId)
                                ? { ...x, Monday: e.target.value, dirty: true }
                                : x
                            )
                          )
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {r.is_calculated ? (
                      <span>{r.Tuesday || ""}</span>
                    ) : (
                      <Input
                        fluid
                        value={r.Tuesday || ""}
                        onChange={(e) =>
                          setDailyTable((prev) =>
                            prev.map((x) =>
                              String(x.statId) === String(r.statId)
                                ? { ...x, Tuesday: e.target.value, dirty: true }
                                : x
                            )
                          )
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {r.is_calculated ? (
                      <span>{r.Wednesday || ""}</span>
                    ) : (
                      <Input
                        fluid
                        value={r.Wednesday || ""}
                        onChange={(e) =>
                          setDailyTable((prev) =>
                            prev.map((x) =>
                              String(x.statId) === String(r.statId)
                                ? {
                                    ...x,
                                    Wednesday: e.target.value,
                                    dirty: true,
                                  }
                                : x
                            )
                          )
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>{formattedTotal}</Table.Cell>
                  <Table.Cell>
                    {/* Per-row save & revert to avoid accidental overwrites */}
                    {!r.is_calculated && (
                      <>
                        <Button
                          size="tiny"
                          color={r.dirty ? "orange" : undefined}
                          onClick={() => saveDailyRow(r)}
                          loading={dailyLoading}
                        >
                          <Icon name="save" />
                          Save Daily
                        </Button>
                        {/* <Button
                          size="tiny"
                          onClick={() => revertDailyRow(r)}
                          disabled={!r.dirty}
                        >
                          <Icon name="undo" />
                          Revert
                        </Button> */}
                      </>
                    )}

                    {/* Save Weekly (always available, even for is_calculated rows).
                        Uses modal prefill so admin can review/edit before saving. */}
                    <Button
                      size="tiny"
                      color="green"
                      onClick={() => openWeeklyModalFromDaily(r)}
                      loading={savingWeeklyIds.includes(String(r.statId))}
                      style={{ marginLeft: 6 }}
                    >
                      <Icon name="calendar check" />
                      Save Weekly
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        <div>
          {dailyMessage && (
            <Message
              positive={dailyMessage.type === "success"}
              negative={dailyMessage.type === "error"}
              content={dailyMessage.text}
            />
          )}
        </div>
        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 12,
            alignItems: "center",
          }}
        >
          <Button primary onClick={saveDailyTable} loading={dailyLoading}>
            Save Modified
          </Button>
          <Button
            onClick={() => loadDailyTable(dailyWeek, statsMeta)}
            disabled={dailyLoading}
          >
            Refresh
          </Button>
        </div>
      </>
    );
  }

  function renderWeeklyPanel() {
    return (
      <>
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Weekending</label>
              <Dropdown
                selection
                options={thursdays.map((d) => ({ key: d, value: d, text: d }))}
                value={weeklyWeek}
                onChange={(_, { value }) => setWeeklyWeek(value)}
              />
            </Form.Field>
          </Form.Group>
        </Form>

        {weeklyLoading ? (
          <Loader active inline="centered" />
        ) : (
          <>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Stat</Table.HeaderCell>

                  <Table.HeaderCell>User</Table.HeaderCell>
                  <Table.HeaderCell>Division</Table.HeaderCell>

                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Value</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(statsMeta || []).map((s) => (
                  <Table.Row key={s.id}>
                    <Table.Cell>
                      <strong>{s.short_id}</strong>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {s.full_name}
                      </div>
                    </Table.Cell>

                    <Table.Cell>{s.username}</Table.Cell>
                    <Table.Cell>{s.division_name}</Table.Cell>

                    <Table.Cell>{s.type}</Table.Cell>
                    <Table.Cell>{weeklyValues[s.id] ?? ""}</Table.Cell>
                    <Table.Cell>
                      <Button size="tiny" onClick={() => openWeeklyModal(s)}>
                        <Icon name="pencil" /> Enter Value
                      </Button>
                      <Button
                        size="tiny"
                        onClick={() => loadWeeklyHistoryForStat(s)}
                      >
                        <Icon name="list" /> History
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {weeklyMessage && (
              <Message
                positive={weeklyMessage.type === "success"}
                negative={weeklyMessage.type === "error"}
                content={weeklyMessage.text}
              />
            )}
            {historyLoading ? (
              <Loader active inline="centered" />
            ) : historyList.length ? (
              <Segment>
                <Header as="h5">History</Header>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Weekending</Table.HeaderCell>
                      <Table.HeaderCell>Value</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {historyList.map((h, i) => (
                      <Table.Row key={i}>
                        <Table.Cell>
                          {h.Weekending || h.week_ending || h.WeekEnding}
                        </Table.Cell>
                        <Table.Cell>{h.Value}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            ) : null}
          </>
        )}
      </>
    );
  }

  return (
    <Container style={{ marginTop: 20 }}>
      <Header as="h2" textAlign="center">
        Input Stats
      </Header>

      {renderTopSwitcher()}

      {viewMode === "daily" ? (
        <Segment>
          <Header as="h4">Daily 7R</Header>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Weekending</label>
                <Dropdown
                  selection
                  options={thursdays.map((d) => ({
                    key: d,
                    value: d,
                    text: d,
                  }))}
                  value={dailyWeek}
                  onChange={(_, { value }) => setDailyWeek(value)}
                />
              </Form.Field>
            </Form.Group>
          </Form>
          {renderDailyTable()}
        </Segment>
      ) : (
        <Segment>
          <Header as="h4">Weekly</Header>
          {renderWeeklyPanel()}
        </Segment>
      )}

      {/* Today's modal */}
      <Modal
        open={todayModalOpen}
        onClose={() => setTodayModalOpen(false)}
        size="small"
      >
        <Modal.Header>
          Enter today's value for{" "}
          {todayModalStat
            ? `${todayModalStat.short_id} — ${todayModalStat.full_name}`
            : ""}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Value</label>
              <Input
                value={todayModalValue}
                onChange={(e) => setTodayModalValue(e.target.value)}
              />
            </Form.Field>
            <p style={{ fontSize: 12, color: "#666" }}>
              This will populate the cell for today based on the selected week.
            </p>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setTodayModalOpen(false)}
            disabled={todayModalSubmitting}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={submitTodayValue}
            loading={todayModalSubmitting}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Weekly modal */}
      <Modal
        open={weeklyModalOpen}
        onClose={() => setWeeklyModalOpen(false)}
        size="small"
      >
        <Modal.Header>
          Enter weekly value for{" "}
          {weeklyModalStat
            ? `${weeklyModalStat.short_id} — ${weeklyModalStat.full_name}`
            : ""}
        </Modal.Header>
        <Modal.Content>
          <Form>
            {/* Removed internal weekending selector — modal now implicitly uses the currently selected week (weeklyWeek) */}
            <Form.Field>
              <label>Value</label>
              <Input
                value={weeklyModalValue}
                onChange={(e) => setWeeklyModalValue(e.target.value)}
              />
            </Form.Field>
            <p style={{ fontSize: 12, color: "#666" }}>
              This will save the value for the currently selected weekending:{" "}
              <strong>{weeklyWeek}</strong>
            </p>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setWeeklyModalOpen(false)}
            disabled={weeklyModalSubmitting}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={submitWeeklySingle}
            loading={weeklyModalSubmitting}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}
